import {view} from '@sail/ui';
import {AnimatePresence} from '@sail/motion';
import {Select} from 'src/v2/components/common';
import {RefundAddressField} from 'src/v2/components/anyCrypto/refund/RefundAddressField';
import {refundRevealAnimation} from 'src/v2/components/anyCrypto/refund/refundRevealAnimation';
import type {RefundMethod} from 'src/v2/state/types';
import type {OpenapiCryptocurrencyPayinsResourceSupportedCurrencyCurrencyNetworkEnum} from '@stripe-internal/data-v1/schema/types';
import {message, useIntl} from '@sail/intl';

const messages = {
  refundMethodLabel: message({
    id: 'crypto.payins.v2.refund.refund_method_label',
    description: 'Label for refund method selector',
    defaultMessage: 'Preferred refund method',
  }),

  returnToSender: message({
    id: 'crypto.payins.v2.refund.return_to_sender',
    description: 'Option to return refund to sender address',
    defaultMessage: 'Return to sender',
  }),

  setNewRefundAddress: message({
    id: 'crypto.payins.v2.refund.set_new_refund_address',
    description: 'Option to set a new refund address',
    defaultMessage: 'Set a new refund address',
  }),
};

type PreferredRefundMethodSectionProps = {
  refundMethod: RefundMethod | null;
  onRefundMethodChange: (method: RefundMethod) => void;
  customRefundAddress: string;
  onRefundAddressChange: (value: string) => void;
  selectedNetwork: OpenapiCryptocurrencyPayinsResourceSupportedCurrencyCurrencyNetworkEnum;
  addressError?: string;
};

export const PreferredRefundMethodSection = ({
  refundMethod,
  onRefundMethodChange,
  customRefundAddress,
  onRefundAddressChange,
  selectedNetwork,
  addressError,
}: PreferredRefundMethodSectionProps) => {
  const intl = useIntl();

  const refundMethodItems = [
    {
      id: 'sendToPaymentAddress',
      label: intl.formatMessage(messages.returnToSender),
    },
    {
      id: 'setNewRefundAddress',
      label: intl.formatMessage(messages.setNewRefundAddress),
    },
  ];

  return (
    <view.div css={{stack: 'y', gap: 'space.150'}}>
      <Select
        variant="form"
        items={refundMethodItems}
        selectedItemId={refundMethod ?? 'sendToPaymentAddress'}
        onItemSelect={(itemId) => onRefundMethodChange(itemId as RefundMethod)}
        label={intl.formatMessage(messages.refundMethodLabel)}
      />
      <AnimatePresence initial={false}>
        {refundMethod === 'setNewRefundAddress' ? (
          <view.div key="refund-address-input" uses={[refundRevealAnimation]}>
            <RefundAddressField
              value={customRefundAddress}
              onChange={onRefundAddressChange}
              selectedNetwork={selectedNetwork}
              error={addressError}
            />
          </view.div>
        ) : null}
      </AnimatePresence>
    </view.div>
  );
};
